import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { EquipmentPositionSummary } from '../../interfaces/MachinePosition.types';
import {
  PositionHistoryPinPopoverContainer,
  PositionHistoryPinPopoverContent,
  PositionHistoryPinPopoverText,
  PositionHistoryPinPopoverTitle,
} from './MachinePositionHistoryMapPinPopover.styles';
import {
  formatPositionHistoryDate,
  getPositionHistoryDurationInDays,
  sortPopoverSummaries,
} from 'app/modules/machine-inventory/utils/positionHistoryUtils';

interface MachinePositionHistoryMapPinPopoverProps {
  equipmentPositionSummaries: EquipmentPositionSummary[];
}

export const MachinePositionHistoryMapPinPopover = ({
  equipmentPositionSummaries,
}: MachinePositionHistoryMapPinPopoverProps): JSX.Element => {
  const { t } = useTranslation();

  const sortedSummaries = useMemo(() => sortPopoverSummaries(equipmentPositionSummaries), [equipmentPositionSummaries]);

  const getDay = (positionSummary: EquipmentPositionSummary): string => {
    const enteredAt = formatPositionHistoryDate(positionSummary.enteredAt);

    if (!positionSummary.exitedAt) {
      return t('machineDetailsPanel.positionHistory.since', { date: enteredAt });
    }

    const exitedAt = formatPositionHistoryDate(positionSummary.exitedAt);

    return `${enteredAt} - ${exitedAt}`;
  };

  const getDuration = (positionSummary: EquipmentPositionSummary): string =>
    t('machineDetailsPanel.positionHistory.dayCount', {
      count: getPositionHistoryDurationInDays(positionSummary),
    });

  return (
    <PositionHistoryPinPopoverContainer>
      <PositionHistoryPinPopoverTitle>{sortedSummaries[0].address || '-'}</PositionHistoryPinPopoverTitle>

      {sortedSummaries.map(positionSummary => (
        <PositionHistoryPinPopoverContent key={positionSummary.id}>
          <PositionHistoryPinPopoverText $hasExitedAt={!!positionSummary.exitedAt}>
            {getDay(positionSummary)}
          </PositionHistoryPinPopoverText>

          <PositionHistoryPinPopoverText $hasExitedAt={!!positionSummary.exitedAt}>
            {getDuration(positionSummary)}
          </PositionHistoryPinPopoverText>
        </PositionHistoryPinPopoverContent>
      ))}
    </PositionHistoryPinPopoverContainer>
  );
};
